import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Typography } from "@mui/material";
import type {
  AICardConfig,
  BenchmarkCardConfig,
  CanvasCard,
  CardConfig,
  CardFilters,
  ChartCardConfig,
  DataFreshnessCardConfig,
  ExposureCardConfig,
  KpiCardConfig,
  MacroBriefCardConfig,
  PerformanceCardConfig,
  SummaryCardConfig,
  TableCardConfig,
  TextCardConfig
} from "store/types";

import { AISettings } from "./AISettings";
import { BenchmarkSettings } from "./BenchmarkSettings";
import { CardFilterSettings } from "./CardFilterSettings";
import { ChartSettings } from "./ChartSettings";
import { DataFreshnessSettings } from "./DataFreshnessSettings";
import { ExposureSettings } from "./ExposureSettings";
import { KpiSettings } from "./KpiSettings";
import { MacroBriefSettings } from "./MacroBriefSettings";
import { PerformanceSettings } from "./PerformanceSettings";
import { SummarySettings } from "./SummarySettings";
import { TableSettings } from "./TableSettings";
import { TextSettings } from "./TextSettings";

interface Props {
  card: CanvasCard;
  onUpdateConfig: (config: CardConfig) => void;
  onUpdateCardFilters: (filters: CardFilters | undefined) => void;
  onClose: () => void;
}

export const SettingsPanel: React.FC<Props> = ({ card, onUpdateConfig, onUpdateCardFilters, onClose }) => {
  const cfg = card.config;
  const isDataCard =
    cfg.type !== "text" && cfg.type !== "ai_card" && cfg.type !== "dataFreshness" && cfg.type !== "macroBrief";

  return (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        bgcolor: "#FFFFFF",
        borderLeft: "1px solid #E5E7EB",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          p: "14px 10px 14px 16px",
          borderBottom: "1px solid #F3F4F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Typography
          sx={{ fontSize: 12, fontWeight: 700, color: "#071F35", textTransform: "uppercase", letterSpacing: "0.05em" }}
        >
          Settings
        </Typography>
        <IconButton onClick={onClose} sx={{ minWidth: 0, p: "2px", color: "#7B92A9", fontSize: 16, lineHeight: 1 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          overflowY: "auto",
          maxHeight: "calc(100vh - 300px)"
        }}
      >
        {cfg.type === "chart" && <ChartSettings config={cfg as ChartCardConfig} onUpdateConfig={onUpdateConfig} />}
        {cfg.type === "kpi" && (
          <KpiSettings
            config={cfg as KpiCardConfig}
            onUpdateConfig={onUpdateConfig}
            widthPercent={card.widthPercent}
            heightPx={card.heightPx}
          />
        )}
        {cfg.type === "summary" && (
          <SummarySettings config={cfg as SummaryCardConfig} onUpdateConfig={onUpdateConfig} />
        )}
        {cfg.type === "table" && <TableSettings config={cfg as TableCardConfig} onUpdateConfig={onUpdateConfig} />}
        {cfg.type === "text" && <TextSettings config={cfg as TextCardConfig} onUpdateConfig={onUpdateConfig} />}
        {cfg.type === "ai_card" && <AISettings config={cfg as AICardConfig} onUpdateConfig={onUpdateConfig} />}
        {cfg.type === "exposure" && (
          <ExposureSettings config={cfg as ExposureCardConfig} onUpdateConfig={onUpdateConfig} />
        )}
        {cfg.type === "performance" && (
          <PerformanceSettings config={cfg as PerformanceCardConfig} onUpdateConfig={onUpdateConfig} />
        )}
        {cfg.type === "benchmark" && (
          <BenchmarkSettings
            config={cfg as BenchmarkCardConfig}
            onUpdateConfig={onUpdateConfig}
            widthPercent={card.widthPercent}
          />
        )}
        {cfg.type === "macroBrief" && (
          <MacroBriefSettings
            config={cfg as MacroBriefCardConfig}
            onUpdateConfig={onUpdateConfig}
            heightPx={card.heightPx}
          />
        )}
        {cfg.type === "dataFreshness" && (
          <DataFreshnessSettings config={cfg as DataFreshnessCardConfig} onUpdateConfig={onUpdateConfig} />
        )}

        {/* Card filters hidden for Performance Attribution for now. */}
        {isDataCard && cfg.type !== "performance" && (
          <CardFilterSettings
            cardFilters={card.config.cardFilters}
            onUpdate={onUpdateCardFilters}
            hideCurrency={cfg.type === "benchmark"}
          />
        )}
      </Box>
    </Box>
  );
};
